import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PostMaturityInterestPanel from '../Common/PostMaturityInterestPanel';

const LoanSchedule = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [postMaturityBreakdown, setPostMaturityBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoanSchedule();
  }, [id]);

  const fetchLoanSchedule = async () => {
    try {
      const response = await api.get(`/officer/loans/${id}/schedule`);
      setLoan(response.data.loan);
      setInstallments(response.data.installments || []);
      setPostMaturityBreakdown(response.data.postMaturityInterestBreakdown || null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching loan schedule:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!loan) {
    return <Typography>Loan not found</Typography>;
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/officer/my-loans')} sx={{ mb: 2 }}>
        Back to My Loans
      </Button>
      <Typography variant="h4" gutterBottom>
        Loan Schedule - Loan #{loan.id}
      </Typography>

      <PostMaturityInterestPanel breakdown={postMaturityBreakdown} />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Loan Details</Typography>
              <Typography><strong>Customer:</strong> {loan.customer?.fullName || loan.customer?.name}</Typography>
              <Typography variant="body1" sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
                <strong>Bank Deposit Amount:</strong> Rs {loan.bankDeposit?.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                (Loan Amount: Rs {loan.approvedAmount?.toFixed(2)} - Document Charges: Rs {loan.documentCharges?.toFixed(2)} - First Installment: Rs {loan.loanTemplate?.firstInstallment?.toFixed(2)})
              </Typography>
              <Typography sx={{ mt: 1 }}><strong>Status:</strong> {loan.status}</Typography>
              <Typography><strong>Disbursement Date:</strong> {loan.disbursementDate ? new Date(loan.disbursementDate).toLocaleDateString() : 'N/A'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Payment Schedule</Typography>
              {loan.loanTemplate && (
                <>
                  <Typography><strong>Template:</strong> {loan.loanTemplate.name}</Typography>
                  <Typography><strong>Total Installments:</strong> {loan.loanTemplate.numberOfInstallments}</Typography>
                  <Typography sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <strong>First Installment (Deducted):</strong> Rs {loan.loanTemplate.firstInstallment?.toFixed(2)}
                  </Typography>
                  <Typography><strong>Remaining Installments:</strong> {loan.loanTemplate.numberOfInstallments - 1} × Rs {loan.loanTemplate.installmentAmount?.toFixed(2)}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Installment Schedule (Remaining Installments)
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>First Installment (Already Deducted):</strong> Rs {loan.loanTemplate?.firstInstallment?.toFixed(2)} - Due on disbursement date
        </Typography>
        <Typography variant="body2" color="textSecondary">
          The first installment and document charges (Rs {loan.documentCharges?.toFixed(2)}) have been deducted from the loan amount. 
          The bank deposit amount of <strong>Rs {loan.bankDeposit?.toFixed(2)}</strong> is what the customer receives.
        </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Paid Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {installments
              .filter(installment => installment.installmentNumber > 1) // Exclude first installment
              .map((installment) => {
                const totalDue = parseFloat(installment.totalAmount || 0);
                const paid = parseFloat(installment.paidAmount || 0);
                const balance = totalDue - paid;
                
                return (
                  <TableRow key={installment.id}>
                    <TableCell>{installment.installmentNumber}</TableCell>
                    <TableCell>{new Date(installment.dueDate).toLocaleDateString()}</TableCell>
                    <TableCell>Rs {installment.amount?.toFixed(2)}</TableCell>
                    <TableCell>Rs {installment.lateInterest?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell>
                      {installment.carryoverAmount > 0 ? (
                        <Typography variant="body2" color="warning.main">
                          Rs {installment.carryoverAmount?.toFixed(2)}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {installment.advanceAmount > 0 ? (
                        <Typography variant="body2" color="info.main">
                          Rs {installment.advanceAmount?.toFixed(2)}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        Rs {totalDue.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>Rs {paid.toFixed(2)}</TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={balance === 0 ? 'success.main' : balance > 0 ? 'error.main' : 'info.main'}
                      >
                        {balance === 0 ? 'Rs 0.00' : balance > 0 ? `Rs ${balance.toFixed(2)}` : `Rs ${Math.abs(balance).toFixed(2)} (Extra)`}
                      </Typography>
                    </TableCell>
                    <TableCell>{installment.status}</TableCell>
                    <TableCell>{installment.paidDate ? new Date(installment.paidDate).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LoanSchedule;

